/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, HStack, Text, Tooltip } from '@chakra-ui/react';
import {
  CQReactTable,
  useCQMediaQuery,
} from '@laxmimanogna/code-quick-components';
import { UrgentIcon } from '../../../../constants/IconData';
import { MyAuditContext } from '../../../../providers/MyAuditProvider';
import { createColumnHelper } from '@tanstack/react-table'; //package installed using yarn -for table
import { format } from 'date-fns';
import {
  createTableProps,
  paginationProps,
} from '../../../../utils/my_audits.utils';
import { renderActionComponent } from '../DashboardComponents';
import { useNavigate } from 'react-router-dom';
import { toTitleCase } from '../../../../utils/common.utils';
import {
  getAuditStatus,
  MY_AUDITS_ORDERING,
} from '../../../../constants/constants';
import FileViewAction from '../FileViewAction';

const columnHelper = createColumnHelper();

function ArchivedTable() {
  const auditContext = useContext(MyAuditContext);
  const { getAuditArchived } = auditContext;
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState('');
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // SCROLL CHANGE: Auto-restore
  const PAGE_NAME = "Auditor";
  const TABLE_NAME = "Archived Audits";

  useEffect(() => {
      if (auditContext.auditArchivedList.length > 0) {
        const itemId = getAuditArchived.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
        if (itemId) {
          setHighlightedRowId(itemId);
          setTimeout(() => {
            setHighlightedRowId(null);
          }, 2000);
        }
      }
    }, [auditContext.auditArchivedList]);

  useEffect(() => {
    let searching = !!(auditContext.filterParams.searchText && auditContext.filterParams.searchText.trim());
    getAuditArchived.loadAPI("Auditor", "Archived Audits", searching);
  }, [auditContext.filterParams, getAuditArchived.showOrdering]);

  useEffect(() => {
    if (MY_AUDITS_ORDERING.includes(ordering)) {
      getAuditArchived.setParams({ ordering });
    }
  }, [ordering]);

  const canScroll = useCQMediaQuery({
    lg: false,
    md: false,
    sm: true,
  });

  let columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        id: 'urgent_column',
        enableSorting: false,
        header: () => '',
        cell: info => {
          return (
            <HStack
              textAlign={'right'}
              justifyContent={'flex-end'}
              alignItems={'end'}
              // spacing={3}
            >
              {info.row.original?.urgent ? (
                <UrgentIcon style={{ width: 20, height: 8 }} />
              ) : (
                <Box ml={2} />
              )}
            </HStack>
          );
        },
      }),

      columnHelper.accessor(row => row, {
        id: 'batch',
        header: () => 'Batch',
        cell: info => {
          const allTooltip = info.getValue().batch_name || info.getValue().batch_id;    
          const tp = { label: allTooltip, placement: 'bottom', hasArrow: true, bg: 'gray.800', color: 'white', borderRadius: 'md', p: 2, openDelay: 80 };
          return (
            <Tooltip {...tp}>
              <Text 
              textAlign={'left'} 
              isTruncated 
              maxW="125px" 
              cursor="default"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              display="block"
              lineHeight="1.2" 
              >
                {info.getValue().batch_name || info.getValue().batch_id}
              </Text>
            </Tooltip>
          );
        },
      }),
      
      columnHelper.accessor('upload_id', {
        header: () => 'Upload #',
        id: 'chart_id',
        cell: info => {
          return (
            <HStack>
              <Text fontSize={16} fontWeight={'bold'}>
                {info.renderValue()}
              </Text>
            </HStack>
          );
        },
      }),

      columnHelper.accessor(row => row.uploaded_date, {
        id: 'upload_date',
        header: () => 'Upload Date',
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'N/A';
          }
          return (
            <Text pl={2} textAlign={'left'} fontSize={16} fontWeight={'bold'}>
              {format(new Date(cellValue), 'MM/dd/yyyy')}
            </Text>
          );
        },
      }),

      columnHelper.accessor('total_page', {
        header: () => 'Pages',
        id: 'total_pages',
        cell: info => {
          return (
            <Text ml={-2} fontSize={16} fontWeight={'bold'}>
              {info.row.original?.total_page}
            </Text>
          );
        },
      }),

      columnHelper.accessor(p => p.specialties?.name, {
        header: () => 'Specialties',
        id: 'specialty__name',
        cell: info => {
          const specialties = info.row.original.specialties?.name;

          if (!specialties) {
            return 'N/A';
          }
          return specialties === 'ent' ? 'ENT' : toTitleCase(specialties);
        },
      }),

      columnHelper.accessor('status', {
        header: () => 'Status',
        id: 'status',
        cell: info => {
          return (
            <Text fontSize={16} fontWeight={'bold'}>
              {getAuditStatus(info.row.original.status)}
            </Text>
          );
        },
      }),
      columnHelper.accessor('actions', {
        header: () => 'Actions',
        enableSorting: false,
        cell: info => {
          return renderActionComponent(
            info.row.original,
            navigate,
            auditContext,
            getAuditArchived, 
            PAGE_NAME, 
            TABLE_NAME
          );
        },
      }),
      columnHelper.accessor('actions', {
        header: () => '',
        enableSorting: false,
        cell: info => {
          const auditUpload = info.row.original;
          return <FileViewAction auditUpload={auditUpload} chart_id={info.row.original?.id}/>;
        },
      }),
    ],
    [auditContext.isAuditUploadListLoading]
  );

  function handlePreviousPage() {
    auditContext.getAuditArchived.loadPrevious("Auditor", "Archived Audits");
  }

  function handleNextPage() {
    auditContext.getAuditArchived.loadNext("Auditor", "Archived Audits");
  }

  function handleSizeChange(size) {
    auditContext.getAuditArchived.loadSize("Auditor", "Archived Audits", size );
  }

  function handlePageChangeByNumber(pageNumber) {
    auditContext.getAuditArchived.loadPageByNumber("Auditor", "Archived Audits", pageNumber);
  }
  
  return (
    <>
      <Box
        // pointerEvents={isUpdating ? 'none' : 'inherit'}
        overflowX={canScroll ? 'scroll' : 'inherit'}
      >
        <CQReactTable
          manualSortBy={true}
          isLoading={auditContext.isAuditUploadListLoading}
          data={auditContext.auditArchivedList}
          columns={columns}
          {...createTableProps(highlightedRowId)}
          {...paginationProps(auditContext.getAuditArchived)}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onSizeChange={handleSizeChange}
          onPageChangeByNumber={handlePageChangeByNumber}
          onAsc={header => {
            setOrdering(header);
          }}
          onDesc={header => {
            setOrdering(`-${header}`);
          }}
        />
      </Box>
    </>
  );
}

export default ArchivedTable;
